<template>
  <v-container class="pa-1" fluid>
    <div>
      <v-row>
        <v-col cols="12">
          <h3>{{ $t("delegation.form.listOfAttachments") }}:</h3>
          <div v-if="delegation.file.length === 0">
            {{ $t("delegation.form.noAttachments") }}
          </div>

          <div :key="item.id" v-for="item in delegation.file">
            <v-card
              v-bind:class="{
                'green accent-1': item.uploadedSize === item.fileSize
              }"
            >
              <v-card-text>
                <v-row no-gutters>
                  <v-col cols="10">
                    <v-row no-gutters>
                      <v-col style="min-width: 40px; max-width: 50px;" cols="1">
                        <v-tooltip top v-if="item.category === 'transport'">
                          <template #activator="{on}">
                            <v-icon v-on="on" x-large>
                              directions_transit
                            </v-icon>
                          </template>
                          {{ $t("delegation.form.fileAttachmentDescription.transport") }}
                        </v-tooltip>
                        <v-tooltip top v-if="item.category === 'accommodation'">
                          <template #activator="{on}">
                            <v-icon v-on="on" x-large>
                              hotel
                            </v-icon>
                          </template>
                          {{ $t("delegation.form.fileAttachmentDescription.accommodation") }}
                        </v-tooltip>
                        <v-tooltip top v-if="item.category === 'attachment'">
                          <template #activator="{on}">
                            <v-icon v-on="on" x-large>
                              attach_file
                            </v-icon>
                          </template>
                          {{ $t("delegation.form.fileAttachmentDescription.manuallyAdded") }}
                        </v-tooltip>
                        <v-tooltip top v-if="item.category === 'otherSummary'">
                          <template #activator="{on}">
                            <v-icon v-on="on" x-large>
                              attach_file
                            </v-icon>
                          </template>
                          {{ $t("delegation.form.fileAttachmentDescription.otherGeneral") }}
                        </v-tooltip>
                      </v-col>
                      <v-col cols="11">
                        <strong style="word-wrap: break-word">{{ item.itemName }}</strong>
                        <br />({{ Number.parseFloat(item.fileSize / 1000).toFixed(1) }} KB) {{ item.status }}
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-spacer />
                  <v-col class="text-right">
                    <v-tooltip top>
                      <template #activator="{on}">
                        <v-btn :disabled="$store.getters['Ajax/loading']" icon v-on="on" @click="downloadFile(item)">
                          <v-icon>cloud_download</v-icon>
                        </v-btn>
                      </template>
                      {{ $t("global.downloadFile") }}
                    </v-tooltip>
                    <v-tooltip top>
                      <template #activator="{on}">
                        <v-btn
                          :disabled="item.category !== 'attachment' || $store.getters['Ajax/loading']"
                          icon
                          v-on="on"
                          @click="removeFile(item)"
                          v-if="$store.state.Delegation.readonly !== true"
                        >
                          <v-icon>close</v-icon>
                        </v-btn>
                      </template>
                      {{ item.category !== "attachment" ? $t("delegation.form.canNotRemoveAttachmentAtItem") : $t("global.removeFile") }}
                    </v-tooltip>
                  </v-col>
                </v-row>
                <v-progress-linear :value="(item.uploadedSize / item.fileSize) * 100" v-if="item.uploadedSize !== item.fileSize" />
              </v-card-text>
            </v-card>
          </div>
          <v-row v-if="$store.state.Delegation.readonly !== true">
            <v-col class="my-3">
              <form ref="inputForm">
                <v-btn class="primary" @click="$refs.fileInput.click()">{{ $t("global.addFile") }}</v-btn>
                <input
                  style="display: none"
                  @change="upload($event)"
                  ref="fileInput"
                  type="file"
                  v-if="uploadButton"
                  :accept="$store.getters['Delegation/allowedAttachmentFileFormat']"
                />
              </form>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12">
          <booking-and-invoices :readonly="$store.state.Delegation.readonly === true" />
        </v-col>

        <v-col style="margin-top: 20px;" cols="12">
          <CollapseOtherSummaryPanel
            :model-other="delegation.other"
            :readonly="$store.state.Delegation.readonly === true"
            :clearable="$store.state.Delegation.readonly !== true"
          />
        </v-col>
        <v-col style="margin-top: 20px;" cols="12">
          <h2>{{ $t("global.summary") }}</h2>
          <div v-if="transportOtherThanDelegationOrderRestrictions">
            <span>
              <b class="red--text">{{ $t("delegation.form.differentTransportWarning") }} </b>
            </span>
          </div>
          <div>
            {{ $t("delegation.form.estimatedTransportCost") }}:
            <span
              ><b
                v-bind:class="{
                  'green--text': !transportCostExceeded,
                  'red--text': transportCostExceeded
                }"
                >{{ transportCost }}</b
              ></span
            >
            <span class="red--text" v-if="transportCostExceeded"
              ><b> ({{ $t("global.error.transportLimitExceeded") }})</b></span
            >
          </div>
          <div>
            {{ $t("delegation.form.estimatedAccommodationCost") }}:
            <span
              ><b
                v-bind:class="{
                  'green--text': !accommodationCostExceeded,
                  'red--text': accommodationCostExceeded
                }"
                >{{ accommodationCost }}</b
              ></span
            >
            <span class="red--text" v-if="accommodationCostExceeded"
              ><b> ({{ $t("global.error.hotelLimitExceeded") }})</b></span
            >
          </div>
          <div>
            {{ $t("delegation.form.estimatedDietCost") }}:
            <span
              ><b class="green--text">{{ countryCost }}</b></span
            >
          </div>
          <div>
            {{ $t("delegation.form.estimatedOtherCost") }}:
            <span
              ><b class="green--text">{{ otherCost }}</b></span
            >
          </div>
          <div>
            {{ $t("delegation.form.estimatedAdvance") }}:
            <span
              ><b class="green--text">{{ payedAdvances }}</b></span
            >
          </div>
          <div>
            <strong>{{ $t("delegation.form.estimatedReturnToDelegated") }}: </strong>
            <span
              ><b class="green--text">{{ returnToWorkerCost }}</b></span
            >
          </div>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import createErrorArray from "../../../repository/errorObjectArray";
import CollapseOtherSummaryPanel from "./other/CollapseOtherSummaryPanel.vue";
import fileDownload from "src/repository/downloadFile";
import BookingAndInvoices from "./BookingAndInvoices";

export default {
  name: "Attachment",
  components: { BookingAndInvoices, CollapseOtherSummaryPanel },
  props: {
    delegation: {
      type: Object,
      required: true
    },
    delegationErrors: {
      type: Object,
      required: false,
      default: function() {
        return createErrorArray(this.delegation);
      }
    }
  },
  data: () => ({
    formControls: {
      additionalCostsShow: false
    },
    additionalCost: [],
    additionalCostInPln: 0,
    uploadButton: true
  }),
  computed: {
    transportCost() {
      let calculation = this.$store.getters["Delegation/delegationCalculation"];
      if (calculation != null && typeof calculation.transportCost !== "undefined") {
        return this.formatCost(calculation.transportCost);
      }
      return this.$t("delegation.form.fillMissingDataToGetResult");
    },
    accommodationCost() {
      let calculation = this.$store.getters["Delegation/delegationCalculation"];
      if (calculation != null && typeof calculation.accommodationCost !== "undefined") return this.formatCost(calculation.accommodationCost);
      return this.$t("delegation.form.fillMissingDataToGetResult");
    },
    countryCost() {
      let calculation = this.$store.getters["Delegation/delegationCalculation"];
      if (calculation != null && typeof calculation.countryCost !== "undefined") return this.formatCost(calculation.countryCost);
      return this.$t("delegation.form.fillMissingDataToGetResult");
    },
    otherCost() {
      let calculation = this.$store.getters["Delegation/delegationCalculation"];
      if (calculation != null && typeof calculation.otherCost !== "undefined") return this.formatCost(calculation.otherCost);
      return this.$t("delegation.form.fillMissingDataToGetResult");
    },
    payedAdvances() {
      let calculation = this.$store.getters["Delegation/delegationCalculation"];
      if (calculation != null && typeof calculation.payedAdvances !== "undefined") return this.formatCost(calculation.payedAdvances);
      return this.$t("delegation.form.fillMissingDataToGetResult");
    },
    returnToWorkerCost() {
      let calculation = this.$store.getters["Delegation/delegationCalculation"];
      if (calculation != null && typeof calculation.returnToWorkerCost !== "undefined") return this.formatCost(calculation.returnToWorkerCost);
      return this.$t("delegation.form.fillMissingDataToGetResult");
    },
    accommodationCostExceeded() {
      if (
        this.$store.state.Delegation.delegationCalculation == null ||
        typeof this.$store.state.Delegation.delegationCalculation.isValidHotelLimit === "undefined"
      ) {
        return false;
      }
      for (let country of this.$store.state.Delegation.delegationCalculation.isValidHotelLimit) {
        for (let isValidHotelLimit of country) {
          if (!isValidHotelLimit) return true;
        }
      }
      return false;
    },
    transportCostExceeded() {
      if (this.$store.state.Delegation.delegationCalculation == null) {
        return false;
      }
      return !this.$store.state.Delegation.delegationCalculation.isValidTransportLimit;
    },
    transportOtherThanDelegationOrderRestrictions() {
      if (this.$store.state.Delegation.transportTypesFromOrder.length > 0) {
        for (let transport of this.$store.state.Delegation.delegationEdited.transport) {
          if (!this.$store.state.Delegation.transportTypesFromOrder.includes(transport.transport_type) && transport.transport_type !== null) {
            return true;
          }
        }
      }
      return false;
    }
  },
  methods: {
    formatCost(cost) {
      return parseFloat(cost).toFixed(2) + " PLN";
    },
    upload(event) {
      const dataToSend = new FormData();
      const file = event.target.files[0];
      if (file.size > 1024 * 1024 * 3) {
        this.$store.dispatch("alertDialog", {
          content: this.$t("components.file.fileToLarge.content"),
          title: this.$t("components.file.fileToLarge.title")
        });
      } else {
        dataToSend.append("filename", "test");
        dataToSend.append("file", file);
        this.uploadButton = false;
        this.$nextTick(() => {
          this.uploadButton = true;
        });
        const newItem = {
          id: this.newId--,
          itemName: file.name,
          status: this.$t("global.loading"),
          fileSize: file.size,
          uploadedSize: 0,
          category: "attachment",
          description: ""
        };
        this.$store.dispatch("Delegation/addFile", newItem);
        const temp = this;
        this.axios({
          url: "file/upload-file",
          data: dataToSend,
          method: "POST",
          onUploadProgress: function(progressEvent) {
            newItem.uploadedSize = progressEvent.loaded;
          }
        })
          .then(function(result) {
            newItem.status = temp.$t("global.loaded");
            newItem.id = result.data.id;
            temp.$store.dispatch("Delegation/validateDelegation");
          })
          .catch(function(error) {
            console.log("Error Result"); // console logi w razie problemów żeby użytkownik mógł opisać problem
            console.log(error.message);
            console.log(error.response);
            console.log(error.request);
            temp.removeFile(newItem);
            temp.$store.dispatch("alertDialog", {
              content: temp.$t("components.file.errorDialog.content") + error.response.status,
              title: temp.$t("components.file.errorDialog.title")
            });
          });
      }
    },

    removeFile(item) {
      this.$store.dispatch("Delegation/removeFile", item.id);
    },

    downloadFile(item) {
      fileDownload("delegation/download-file?fileId=" + item.id);
    }
  }
};
</script>

<style scoped></style>
